import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Box,
  Avatar,
  Badge,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  CloudUpload, 
  Send, 
  Download, 
  Visibility, 
  CheckCircle, 
  Error, 
  Add, 
  Delete, 
  Person, 
  Group,
  Business,
  Search,
  CheckCircleOutline,
  Warning
} from '@mui/icons-material';
import { firmaApi, adminApi } from '../services/api.ts';

interface DocumentData {
  titulo: string;
  descripcion: string;
  categoria: string;
  fechaExpiracion: string;
  firmantesMinimos: number;
}

interface FirmanteData {
  email: string;
  nombre: string;
  telefono: string;
  tipoDocumento: string;
  numeroDocumento: string;
  esEmpresa: boolean;
  cuit?: string;
  documentoPersoneria?: string;
}

interface Documento {
  id: string;
  titulo: string;
  estado: string;
  firmante: string;
  email: string;
  fechaCreacion: string;
  fechaExpiracion: string;
}

const AdminPanel: React.FC = () => {
  console.log('🔧 AdminPanel se está renderizando - VERSIÓN CON CUIT Y DOCUMENTO');
  
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentData, setDocumentData] = useState<DocumentData>({
    titulo: '',
    descripcion: '',
    categoria: '',
    fechaExpiracion: '',
    firmantesMinimos: 1
  });
  const [firmantes, setFirmantes] = useState<FirmanteData[]>([]);
  const [currentFirmante, setCurrentFirmante] = useState<FirmanteData>({
    email: '',
    nombre: '',
    telefono: '',
    tipoDocumento: 'DNI',
    numeroDocumento: '',
    esEmpresa: false,
    cuit: '',
    documentoPersoneria: ''
  });
  const [documentos, setDocumentos] = useState<Documento[]>([]);
  const [linkGenerado, setLinkGenerado] = useState<string>('');
  const [buscandoFirmante, setBuscandoFirmante] = useState(false);
  const [firmanteEncontrado, setFirmanteEncontrado] = useState<any>(null);
  const [mostrarConfirmacion, setMostrarConfirmacion] = useState(false);
  const [showAddFirmanteDialog, setShowAddFirmanteDialog] = useState(false);

  const steps = ['Nuevo Documento', 'Configurar Firmantes', 'Generar Links y Enviar'];

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setSelectedFile(file);
      setError('');
    } else {
      setError('Por favor selecciona un archivo PDF válido');
    }
  };

  const handleNext = () => {
    if (activeStep === 0 && !selectedFile) {
      setError('Por favor selecciona un archivo PDF');
      return;
    }
    if (activeStep === 0 && !documentData.titulo) {
      setError('Por favor ingresa el título del documento');
      return;
    }
    if (activeStep === 1 && firmantes.length < documentData.firmantesMinimos) {
      setError(`Debes agregar al menos ${documentData.firmantesMinimos} firmante(s)`);
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setError('');
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setError('');
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      // Convertir archivo a base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Data = reader.result as string;
        const base64Content = base64Data.split(',')[1];

        const requestData = {
          archivo: base64Content,
          titulo: documentData.titulo,
          descripcion: documentData.descripcion,
          categoria: documentData.categoria,
          fechaExpiracion: documentData.fechaExpiracion,
          firmantes: JSON.stringify(firmantes)
        };

        const response = await adminApi.subirDocumentoMultiFirmantes(requestData);
        console.log('Respuesta del backend:', response);
        setSuccess('Documento subido exitosamente con múltiples firmantes');
        
        // Manejar la respuesta de forma segura
        if (response.data && response.data.success && response.data.data && response.data.data.links && response.data.data.links.length > 0) {
          setLinkGenerado(response.data.data.links[0].link);
        } else {
          setLinkGenerado('Documento subido exitosamente');
        }
        
        // Reset form
        setSelectedFile(null);
        setDocumentData({
          titulo: '',
          descripcion: '',
          categoria: '',
          fechaExpiracion: '',
          firmantesMinimos: 1
        });
        setFirmantes([]);
        setActiveStep(0);
      };
      reader.readAsDataURL(selectedFile!);
    } catch (err: any) {
      setError('Error al subir documento: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'PENDIENTE': return 'warning';
      case 'FIRMANDO': return 'info';
      case 'FIRMADO': return 'success';
      case 'DESCARGADO': return 'success';
      default: return 'default';
    }
  };

  const buscarFirmanteExistente = async () => {
    if (!currentFirmante.email && !currentFirmante.numeroDocumento) {
      setError('Por favor ingresa al menos el email o número de documento');
      return;
    }

    try {
      setBuscandoFirmante(true);
      setError('');
      const response = await adminApi.buscarFirmante(currentFirmante.email, currentFirmante.numeroDocumento);
      
      if (response.data && response.data.success) {
        const clientes = response.data.data;
        const firmanteEncontrado = clientes.find((cliente: any) => {
          const emailMatch = !currentFirmante.email || cliente.email === currentFirmante.email;
          const documentoMatch = !currentFirmante.numeroDocumento || cliente.numeroDocumento === currentFirmante.numeroDocumento;
          return emailMatch && documentoMatch;
        });

        if (firmanteEncontrado) {
          setFirmanteEncontrado(firmanteEncontrado);
          setMostrarConfirmacion(true);
        } else {
          setFirmanteEncontrado(null);
          setMostrarConfirmacion(false);
        }
      }
    } catch (error: any) {
      console.error('Error al buscar firmante:', error);
      setError('Error al buscar firmante: ' + (error.response?.data?.message || error.message));
    } finally {
      setBuscandoFirmante(false);
    }
  };

  const usarFirmanteExistente = () => {
    if (firmanteEncontrado) {
      setCurrentFirmante({
        email: firmanteEncontrado.email,
        nombre: firmanteEncontrado.nombre,
        telefono: firmanteEncontrado.telefono,
        tipoDocumento: firmanteEncontrado.tipoDocumento,
        numeroDocumento: firmanteEncontrado.numeroDocumento,
        esEmpresa: firmanteEncontrado.esEmpresa,
      });
      setFirmanteEncontrado(null);
      setMostrarConfirmacion(false);
    }
  };

  const continuarConNuevoFirmante = () => {
    setFirmanteEncontrado(null);
    setMostrarConfirmacion(false);
  };

  const agregarFirmante = () => {
    if (!currentFirmante.email || !currentFirmante.nombre || !currentFirmante.telefono || !currentFirmante.numeroDocumento) {
      setError('Por favor completa todos los campos obligatorios del firmante');
      return;
    }

    // Validar CUIT para empresas
    if (currentFirmante.esEmpresa && !currentFirmante.cuit) {
      setError('El CUIT es obligatorio para empresas');
      return;
    }

    // Verificar si el firmante ya existe
    const firmanteExistente = firmantes.find(f => f.email === currentFirmante.email || f.numeroDocumento === currentFirmante.numeroDocumento);
    if (firmanteExistente) {
      setError('Este firmante ya ha sido agregado');
      return;
    }

    setFirmantes([...firmantes, { ...currentFirmante }]);
    setCurrentFirmante({
      email: '',
      nombre: '',
      telefono: '',
      tipoDocumento: 'DNI',
      numeroDocumento: '',
      esEmpresa: false,
      cuit: '',
      documentoPersoneria: ''
    });
    setShowAddFirmanteDialog(false);
    setError('');
  };

  const eliminarFirmante = (index: number) => {
    setFirmantes(firmantes.filter((_, i) => i !== index));
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 4, textAlign: 'center', color: 'primary.main' }}>
              Información del Documento
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ mb: 2, color: 'text.primary' }}>
                  📄 Subir Documento PDF
                </Typography>
                <Paper
                  sx={{
                    border: '2px dashed #ccc',
                    p: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': { 
                      borderColor: 'primary.main',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                  onClick={() => document.getElementById('file-input')?.click()}
                >
                  <input
                    id="file-input"
                    type="file"
                    accept=".pdf"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  <CloudUpload sx={{ fontSize: 64, color: selectedFile ? 'success.main' : '#666', mb: 2 }} />
                  <Typography variant="h6" color="textSecondary" sx={{ mb: 1 }}>
                    {selectedFile ? selectedFile.name : 'Haz clic para seleccionar un archivo PDF'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Solo archivos PDF son permitidos
                  </Typography>
                </Paper>
              </Box>
              
              <TextField
                fullWidth
                label="Título del documento *"
                value={documentData.titulo}
                onChange={(e) => setDocumentData({...documentData, titulo: e.target.value})}
                required
                placeholder="Ej: Contrato de Servicios"
                variant="outlined"
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
              
              <TextField
                fullWidth
                label="Descripción (opcional)"
                multiline
                rows={4}
                value={documentData.descripcion}
                onChange={(e) => setDocumentData({...documentData, descripcion: e.target.value})}
                placeholder="Descripción breve del documento..."
                variant="outlined"
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
              
              <FormControl fullWidth>
                <InputLabel>Categoría</InputLabel>
                <Select
                  value={documentData.categoria}
                  onChange={(e) => setDocumentData({...documentData, categoria: e.target.value})}
                  variant="outlined"
                  size="medium"
                  sx={{ 
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    },
                  }}
                >
                  <MenuItem value="CONTRATO">📋 Contrato</MenuItem>
                  <MenuItem value="ACUERDO">🤝 Acuerdo</MenuItem>
                  <MenuItem value="CONVENIO">📝 Convenio</MenuItem>
                  <MenuItem value="CERTIFICADO">🏆 Certificado</MenuItem>
                  <MenuItem value="OTRO">📄 Otro</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                type="number"
                label="Cantidad mínima de firmantes *"
                value={documentData.firmantesMinimos}
                onChange={(e) => setDocumentData({...documentData, firmantesMinimos: parseInt(e.target.value) || 1})}
                required
                inputProps={{ min: 1, max: 10 }}
                variant="outlined"
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
              
              <TextField
                fullWidth
                type="date"
                label="Fecha de expiración (opcional)"
                value={documentData.fechaExpiracion}
                onChange={(e) => setDocumentData({...documentData, fechaExpiracion: e.target.value})}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="medium"
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                }}
              />
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 4, textAlign: 'center', color: 'primary.main' }}>
              Configurar Firmantes
            </Typography>

            {/* Resumen de firmantes */}
            <Box sx={{ mb: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Group sx={{ mr: 1 }} />
                  Firmantes Agregados
                </Typography>
                <Chip 
                  label={`${firmantes.length}/${documentData.firmantesMinimos} mínimo`}
                  color={firmantes.length >= documentData.firmantesMinimos ? 'success' : 'warning'}
                  icon={firmantes.length >= documentData.firmantesMinimos ? <CheckCircleOutline /> : <Warning />}
                />
              </Box>
              
              {firmantes.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                  No hay firmantes agregados. Haz clic en "Agregar Firmante" para comenzar.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {firmantes.map((firmante, index) => (
                    <Grid item xs={12} md={6} key={index}>
                      <Card sx={{ 
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': { borderColor: 'primary.main' }
                      }}>
                        <CardContent sx={{ p: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Avatar sx={{ 
                                width: 40, 
                                height: 40, 
                                mr: 2,
                                bgcolor: firmante.esEmpresa ? 'secondary.main' : 'primary.main'
                              }}>
                                {firmante.esEmpresa ? <Business /> : <Person />}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  {firmante.nombre}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  {firmante.email}
                                </Typography>
                                <Typography variant="caption" color="textSecondary">
                                  {firmante.tipoDocumento}: {firmante.numeroDocumento}
                                </Typography>
                              </Box>
                            </Box>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => eliminarFirmante(index)}
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>

            {/* Botón para agregar firmante */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setShowAddFirmanteDialog(true)}
                size="large"
                sx={{ px: 4 }}
              >
                Agregar Firmante
              </Button>
            </Box>

            {/* Dialog para agregar firmante */}
            <Dialog 
              open={showAddFirmanteDialog} 
              onClose={() => setShowAddFirmanteDialog(false)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Person sx={{ mr: 1 }} />
                  Agregar Nuevo Firmante
                </Typography>
              </DialogTitle>
              <DialogContent>
                <Box sx={{ pt: 2 }}>
                  {/* Búsqueda de firmante existente */}
                  <Box sx={{ mb: 4, p: 3, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.200' }}>
                    <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
                      <Search sx={{ mr: 1 }} />
                      Buscar Firmante Existente
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                      Ingresa el email o número de documento para buscar si el firmante ya existe en el sistema
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={5}>
                        <TextField
                          fullWidth
                          label="Email del firmante"
                          type="email"
                          value={currentFirmante.email}
                          onChange={(e) => setCurrentFirmante({...currentFirmante, email: e.target.value})}
                          placeholder="firmante@ejemplo.com"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <TextField
                          fullWidth
                          label="Número de documento"
                          value={currentFirmante.numeroDocumento}
                          onChange={(e) => setCurrentFirmante({...currentFirmante, numeroDocumento: e.target.value})}
                          placeholder="12345678"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={buscarFirmanteExistente}
                          disabled={buscandoFirmante || (!currentFirmante.email && !currentFirmante.numeroDocumento)}
                          size="small"
                          startIcon={buscandoFirmante ? <CircularProgress size={16} /> : <Search />}
                        >
                          {buscandoFirmante ? 'Buscando...' : 'Buscar'}
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Resultado de búsqueda */}
                  {firmanteEncontrado && mostrarConfirmacion && (
                    <Box sx={{ mb: 4, p: 3, bgcolor: 'success.50', borderRadius: 2, border: '1px solid', borderColor: 'success.200' }}>
                      <Typography variant="h6" color="success.main" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        <CheckCircle sx={{ mr: 1 }} />
                        Firmante Encontrado
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Se encontró un firmante con los datos ingresados:
                      </Typography>
                      <Box sx={{ mb: 2, p: 2, bgcolor: 'white', borderRadius: 1 }}>
                        <Typography variant="body2"><strong>Nombre:</strong> {firmanteEncontrado.nombre}</Typography>
                        <Typography variant="body2"><strong>Email:</strong> {firmanteEncontrado.email}</Typography>
                        <Typography variant="body2"><strong>Teléfono:</strong> {firmanteEncontrado.telefono}</Typography>
                        <Typography variant="body2"><strong>Documento:</strong> {firmanteEncontrado.tipoDocumento} - {firmanteEncontrado.numeroDocumento}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          color="success"
                          onClick={usarFirmanteExistente}
                          size="small"
                          startIcon={<CheckCircle />}
                        >
                          Usar Firmante Existente
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={continuarConNuevoFirmante}
                          size="small"
                        >
                          Continuar con Nuevo Firmante
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {/* Formulario de firmante */}
                  <Typography variant="h6" gutterBottom>
                    📝 Datos del Firmante
                  </Typography>
                  
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Email del firmante *"
                        type="email"
                        value={currentFirmante.email}
                        onChange={(e) => setCurrentFirmante({...currentFirmante, email: e.target.value})}
                        required
                        placeholder="firmante@ejemplo.com"
                        variant="outlined"
                        size="medium"
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Nombre completo *"
                        value={currentFirmante.nombre}
                        onChange={(e) => setCurrentFirmante({...currentFirmante, nombre: e.target.value})}
                        required
                        placeholder="Nombre y apellido del firmante"
                        variant="outlined"
                        size="medium"
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Teléfono *"
                        value={currentFirmante.telefono}
                        onChange={(e) => setCurrentFirmante({...currentFirmante, telefono: e.target.value})}
                        required
                        placeholder="+54 9 11 1234-5678"
                        variant="outlined"
                        size="medium"
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Tipo de documento</InputLabel>
                        <Select
                          value={currentFirmante.tipoDocumento}
                          onChange={(e) => setCurrentFirmante({...currentFirmante, tipoDocumento: e.target.value})}
                          variant="outlined"
                          size="medium"
                        >
                          <MenuItem value="DNI">🆔 DNI</MenuItem>
                          <MenuItem value="PASAPORTE">📘 Pasaporte</MenuItem>
                          <MenuItem value="LICENCIA">🚗 Licencia de conducir</MenuItem>
                          <MenuItem value="CUIT">🏢 CUIT</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Número de documento *"
                        value={currentFirmante.numeroDocumento}
                        onChange={(e) => setCurrentFirmante({...currentFirmante, numeroDocumento: e.target.value})}
                        required
                        placeholder="12345678"
                        variant="outlined"
                        size="medium"
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        height: '100%',
                        pl: 1
                      }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={currentFirmante.esEmpresa}
                              onChange={(e) => {
                                console.log('🔧 Debug - Checkbox cambiado a:', e.target.checked);
                                setCurrentFirmante({...currentFirmante, esEmpresa: e.target.checked});
                              }}
                            />
                          }
                          label="Es una empresa"
                        />
                      </Box>
                    </Grid>

                    {/* Campos específicos para empresas */}
                    {console.log('🔧 Debug - currentFirmante.esEmpresa:', currentFirmante.esEmpresa)}
                    {currentFirmante.esEmpresa === true && (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="CUIT *"
                            value={currentFirmante.cuit || ''}
                            onChange={(e) => setCurrentFirmante({...currentFirmante, cuit: e.target.value})}
                            required
                            placeholder="20-12345678-9"
                            variant="outlined"
                            size="medium"
                            helperText="CUIT es obligatorio para empresas"
                          />
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" gutterBottom color="primary" sx={{ mt: 2 }}>
                            📄 Documento de Personería Jurídica (Obligatorio para empresas)
                          </Typography>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const reader = new FileReader();
                                reader.onload = (event) => {
                                  const base64 = event.target?.result as string;
                                  setCurrentFirmante(prev => ({
                                    ...prev,
                                    documentoPersoneria: base64.split(',')[1] // Remover el prefijo
                                  }));
                                };
                                reader.readAsDataURL(file);
                              }
                            }}
                            style={{ marginBottom: '16px' }}
                          />
                          <Typography variant="caption" display="block" color="textSecondary">
                            Sube el documento que acredite la personería jurídica de la empresa (PDF, JPG, PNG)
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowAddFirmanteDialog(false)}>
                  Cancelar
                </Button>
                <Button 
                  variant="contained" 
                  onClick={agregarFirmante}
                  startIcon={<Add />}
                >
                  Agregar Firmante
                </Button>
              </DialogActions>
            </Dialog>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 4, textAlign: 'center', color: 'primary.main' }}>
              Resumen y Envío
            </Typography>
            
            <Card sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  📄 Documento: {documentData.titulo}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  {documentData.descripcion}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={`Categoría: ${documentData.categoria}`} size="small" />
                  <Chip label={`Firmantes: ${firmantes.length}`} size="small" color="primary" />
                  {documentData.fechaExpiracion && (
                    <Chip label={`Expira: ${new Date(documentData.fechaExpiracion).toLocaleDateString()}`} size="small" color="warning" />
                  )}
                </Box>
              </CardContent>
            </Card>

            
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 4, textAlign: 'center', color: 'primary.main' }}>
        Panel de Administración
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 4 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent(activeStep)}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            variant="outlined"
          >
            Atrás
          </Button>
          <Box>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <Send />}
              >
                {loading ? 'Procesando...' : 'Subir Documento'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={loading}
              >
                Siguiente
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {linkGenerado && (
        <Paper sx={{ p: 3, mt: 3, bgcolor: 'success.50' }}>
          <Typography variant="h6" gutterBottom color="success.main">
            ✅ Documento subido exitosamente
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Link generado: {linkGenerado}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigator.clipboard.writeText(linkGenerado)}
            size="small"
          >
            Copiar Link
          </Button>
        </Paper>
      )}
    </Container>
  );
};

export default AdminPanel; 